import React, { useState } from "react";
import { Palette, Loader2, Sparkles, Copy, Check, RefreshCw, ChevronLeft, ChevronRight, Download } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import api from "../lib/apiClient";
import { downloadDataUrl, slugFilename } from "../lib/download";

export default function BrandKit({ plan, value, onChange, readOnly }) {
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(null);

  const generate = async (useOrder = false) => {
    setLoading(true);
    try {
      const body = useOrder && value ? { colors: value.colors, font: value.font, tagline: value.tagline } : undefined;
      const res = await api.post(`/plans/${plan.plan_id}/brandkit`, body);
      onChange && onChange(res.data);
      toast.success("Brand kit generated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Brand kit failed");
    } finally {
      setLoading(false);
    }
  };

  const move = (i, dir) => {
    const j = i + dir;
    if (!value?.colors || j < 0 || j >= value.colors.length) return;
    const arr = [...value.colors];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    onChange && onChange({ ...value, colors: arr });
  };

  const copyHex = (hex) => {
    navigator.clipboard.writeText(hex);
    setCopied(hex);
    setTimeout(() => setCopied(null), 1500);
  };

  const hasKit = value && value.colors && value.colors.length > 0;

  return (
    <div data-testid="brand-kit" className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Palette className="h-5 w-5 text-violet-400" />
          <h3 className="font-display text-lg font-bold">Brand Kit</h3>
        </div>
        {!readOnly && (
          <Button data-testid="brandkit-generate-button" size="sm" onClick={() => generate(false)} disabled={loading} className="cta-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-4 w-4 mr-1" /> {hasKit ? "Regenerate" : "Generate brand kit"}</>}
          </Button>
        )}
      </div>

      {!hasKit ? (
        <p className="text-sm text-slate-500 italic py-6 text-center">
          {readOnly ? "No brand kit yet." : "Turn your logo into a matching colour palette, font, tagline and a real brand banner."}
        </p>
      ) : (
        <div className="space-y-5">
          {value.banner && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase text-slate-500">Brand banner</div>
                <Button data-testid="brandkit-download-banner" size="sm" variant="outline" onClick={() => downloadDataUrl(value.banner, slugFilename(plan.business_name, "banner"))} className="border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-100 h-7">
                  <Download className="h-3.5 w-3.5 mr-1" /> Download
                </Button>
              </div>
              <img data-testid="brandkit-banner" src={value.banner} alt="brand banner" className="w-full rounded-xl border border-slate-800" />
            </div>
          )}
          {value.tagline && (
            <div>
              <div className="text-xs uppercase text-slate-500 mb-1">Tagline</div>
              <div data-testid="brandkit-tagline" className="font-display text-xl font-bold text-white">"{value.tagline}"</div>
            </div>
          )}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase text-slate-500">Palette</div>
              {!readOnly && (
                <Button data-testid="brandkit-refresh-order" size="sm" variant="outline" onClick={() => generate(true)} disabled={loading} className="border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-100 h-7">
                  {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh with this order</>}
                </Button>
              )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {value.colors.map((c, i) => (
                <div key={i} data-testid={`brandkit-color-${i}`} className="rounded-xl border border-slate-800 overflow-hidden">
                  <div className="h-16 w-full cursor-pointer" style={{ background: c.hex }} onClick={() => !readOnly && copyHex(c.hex)} />
                  <div className="p-2 bg-slate-950/60">
                    <div className="text-xs font-semibold text-slate-200">{c.name}</div>
                    <div className="flex items-center justify-between text-[11px] text-slate-400 uppercase">
                      <span className="flex items-center gap-1">{c.hex}{copied === c.hex && <Check className="h-3 w-3 text-emerald-400" />}</span>
                      {!readOnly && (
                        <span className="flex items-center gap-1">
                          <button data-testid={`brandkit-move-left-${i}`} onClick={() => move(i, -1)} className="hover:text-white disabled:opacity-30" disabled={i === 0}><ChevronLeft className="h-3.5 w-3.5" /></button>
                          <button data-testid={`brandkit-move-right-${i}`} onClick={() => move(i, 1)} className="hover:text-white disabled:opacity-30" disabled={i === value.colors.length - 1}><ChevronRight className="h-3.5 w-3.5" /></button>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          {value.font && (
            <div>
              <div className="text-xs uppercase text-slate-500 mb-1">Suggested font</div>
              <div data-testid="brandkit-font" className="text-slate-200">{value.font}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
